import React from 'react';
import { useTelephony } from './TelephonyProvider';

interface ClickToCallProps {
  /** The phone number to display and dial. Renders a dash when absent. */
  number?: string | null;
  /** Person's name — shown in the "Calling …" toast. */
  name?: string;
  /**
   * CMS record id (studentId / teacherId). Stored on the call so call history
   * joins back to this record.
   */
  sourceRecordId?: string;
  /** Customer's CMS userId (distinct from sourceRecordId) — cross-system reporting joins. */
  customerUserId?: string;
  /**
   * Originating tool stamped on the call log. Defaults to 'admin'; the RM
   * Dashboard passes 'tasks' (with sourceRecordId = taskId) so artium-tasks
   * auto-links the finished call to the task via webhook.
   */
  source?: string;
  /**
   * Keep the call button visible even when this login isn't enrolled as a
   * telephony agent: the click pops the in-place "Enable calling" widget
   * (no navigation) and the call starts right after setup. Default false
   * preserves the historical plain-number fallback on the older pages.
   */
  fallbackToDialer?: boolean;
  /**
   * Table cells want the button pinned to the column's right edge (default);
   * headers/detail views want it snug beside the number — pass inline.
   */
  inline?: boolean;
  /** Buttons only — for headers that show the number elsewhere. */
  hideNumber?: boolean;
  /** 'lg' = larger buttons, e.g. beside a name in a detail header. */
  size?: 'md' | 'lg';
  className?: string;
}

const buttonStyle: React.CSSProperties = {
  border: 'none',
  borderRadius: 999,
  width: 26,
  height: 26,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  padding: 0,
  lineHeight: 0,
};

/**
 * Drop-in replacement for anywhere the CMS prints a phone number: renders the
 * number with a call button that dials through Artium Telephony.
 *
 *   <ClickToCall number={student.mobileNumber} name={student.studentName}
 *                sourceRecordId={student.studentId} />
 */
const ClickToCall: React.FC<ClickToCallProps> = ({
  number,
  name,
  sourceRecordId,
  customerUserId,
  source,
  fallbackToDialer,
  inline,
  hideNumber,
  size = 'md',
  className,
}) => {
  const big = size === 'lg';
  const sized: React.CSSProperties = big ? { width: 34, height: 34 } : {};
  const { enabled, enrolled, whatsappAllowed, requestCall } = useTelephony();

  if (!number || !number.trim()) {
    return <span className={className}>—</span>;
  }

  if (!enabled || (!enrolled && !fallbackToDialer)) {
    return <span className={className}>{number}</span>;
  }

  // Never navigates: enrolled agents dial in the dock; unenrolled ones get
  // the in-place enable-calling widget, then the call starts automatically.
  // customerUserId (upstream) rides along so call logs join back to the CMS user.
  const dial = (channel?: 'WHATSAPP'): void =>
    requestCall(number, { name, sourceRecordId, source, customerUserId, channel });

  return (
    <span
      className={className}
      style={{
        display: inline ? 'inline-flex' : 'flex',
        alignItems: 'center',
        gap: 6,
        width: inline ? 'auto' : '100%',
      }}
    >
      {!hideNumber && number}
      {/* marginLeft auto pins every button to the column's right edge, so
          they align vertically regardless of number length */}
      <button
        type="button"
        title={`Call ${name || number}`}
        aria-label={`Call ${name || number}`}
        onClick={(e) => {
          // Number rows often live inside clickable cards/links.
          e.preventDefault();
          e.stopPropagation();
          dial();
        }}
        style={{
          // Miniature of the floating call bubble (TelephonyProvider) so
          // every call affordance in the CMS reads the same.
          ...buttonStyle,
          ...sized,
          background: 'linear-gradient(180deg, #3ed49c 0%, #14b87f 100%)',
          marginLeft: inline ? 0 : 'auto',
          boxShadow: '0 2px 8px rgba(20, 184, 127, .4)',
        }}
      >
        {/* Icon font, not svg: several feature pages ship CSS that resizes
            or distorts table svgs, and an inline glyph is immune to it. */}
        <i
          className="fa fa-phone"
          aria-hidden
          style={{ color: '#fff', fontSize: big ? 16 : 13, lineHeight: 1 }}
        />
      </button>
      {whatsappAllowed && (
        <button
          type="button"
          title={`WhatsApp call ${name || number}`}
          aria-label={`WhatsApp call ${name || number}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dial('WHATSAPP');
          }}
          style={{
            // WhatsApp brand green so the two paths are unmistakable at a
            // glance. Rendered only for agents flagged for the WA rollout.
            ...buttonStyle,
            ...sized,
            background: 'linear-gradient(180deg, #34e07a 0%, #1eb954 100%)',
            boxShadow: '0 2px 8px rgba(37, 211, 102, .4)',
          }}
        >
          <i
            className="fa fa-whatsapp"
            aria-hidden
            style={{ color: '#fff', fontSize: big ? 18 : 14, lineHeight: 1 }}
          />
        </button>
      )}
    </span>
  );
};

export default ClickToCall;
